import { Link, useLocation } from 'react-router-dom';
import { Plus, Filter, Search, Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { leaveApi } from '../../services/api';

interface LeaveApplication {
  applicationId: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  status: string;
  submittedAt: string;
}

export default function MyLeavePage() {
  const location = useLocation();
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [applications, setApplications] = useState<LeaveApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Check for success message from navigation
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear message from history state
      window.history.replaceState({}, document.title);
      // Auto-hide after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchApplications = async () => {
      setIsLoading(true);
      setError('');

      try {
        const params: Record<string, string> = {};
        if (filter !== 'all') {
          if (filter === 'pending') {
            params.status = 'PENDING_RECOMMENDATION,PENDING_APPROVAL';
          } else {
            params.status = filter.toUpperCase();
          }
        }

        const response = await leaveApi.listApplications(params) as { applications: LeaveApplication[] };
        setApplications(response.applications || []);
      } catch (err: any) {
        console.error('Error fetching applications:', err);
        setError(err.message || 'Failed to load leave applications');
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplications();
  }, [filter]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'PENDING_APPROVAL':
      case 'PENDING_RECOMMENDATION':
        return 'bg-yellow-100 text-yellow-800';
      case 'DECLINED':
        return 'bg-red-100 text-red-800';
      case 'CANCELLED':
      case 'CANCELLATION_PENDING':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatLeaveType = (type: string) => {
    return type.charAt(0) + type.slice(1).toLowerCase();
  };

  // Filter applications based on search term
  const filteredApplications = applications.filter(app => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      app.leaveType.toLowerCase().includes(search) ||
      app.startDate.includes(search) ||
      app.endDate.includes(search)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Leave</h1>
          <p className="text-sm text-gray-600">View and manage your leave applications</p>
        </div>
        <Link to="/leave/apply" className="btn-primary flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Apply for Leave
        </Link>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4 p-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className="input w-40"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex flex-1 items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search applications..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="input max-w-xs"
            />
          </div>
        </div>
      </div>

      {/* Applications List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            <span className="ml-2 text-gray-600">Loading applications...</span>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-gray-500">
              {searchTerm || filter !== 'all'
                ? 'No applications match your filters'
                : 'No leave applications yet'}
            </p>
            {!searchTerm && filter === 'all' && (
              <Link to="/leave/apply" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
                Apply for your first leave
              </Link>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Leave Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Days
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Submitted
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredApplications.map(app => (
                <tr key={app.applicationId} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="font-medium text-gray-900">
                      {formatLeaveType(app.leaveType)} Leave
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {app.startDate} - {app.endDate}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {app.totalDays} {app.totalDays === 1 ? 'day' : 'days'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(
                        app.status
                      )}`}
                    >
                      {formatStatus(app.status)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {app.submittedAt ? new Date(app.submittedAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                    <Link
                      to={`/leave/${app.applicationId}`}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
